import { useQuery } from '@apollo/client';
import {
    ButtonDock,
    getAchievementCategoryTemplateId,
    liquidGlassRowShadowSx,
    liquidGlassSurfaceFillSx,
    miniButtonDockScrollBottomPad,
    setTemplateTranslations,
    translateTemplateText,
} from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Placeholder, Text } from '@telegram-apps/telegram-ui';
import { Page } from 'components/Page';
import dayjs from 'dayjs';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    GetCategoriesQuery,
    GetCategoryAchievementsQuery,
    GetCategoryAchievementsQueryVariables,
    GetTemplatesQuery,
} from '../client/generated/graphql';
import { getCategoryAchievements } from '../client/queries/achievement';
import { getCategoriesQuery } from '../client/queries/category';
import { getTemplatesQuery } from '../client/queries/template';
import { AchievementListItem } from '../components/achievements/AchievementListItem';
import { AchievementListSkeleton } from '../components/achievements/skeleton/AchievementListSkeleton';
import { CategoryIcon } from '../components/common/CategoryIcon';
import { FocusLeePlaceholderImage } from '../components/common/FocusLeePlaceholderImage';
import { miniApp, retrieveLaunchParams } from '@tma.js/sdk';
import { getColor } from '../helpers/color';
import { getCategoryExternalTemplate } from '../helpers/externalCategory';
import { openExternalLink } from '../helpers/externalLink';
import { useCachedQuestsWithFallback } from '../hooks/caching/quest';
import { achievementCreatePath, achievementViewPath } from '../navigation/paths';

export const AchievementListPage: FC = () => {
    const { t } = useTranslation();
    const { categoryId } = useParams<{ categoryId: string }>();
    const navigate = useNavigate();
    const externalTemplateCategoryId = getAchievementCategoryTemplateId(retrieveLaunchParams().tgWebAppStartParam);
    const isExternalCategoryDeepLink = categoryId === externalTemplateCategoryId;

    const {
        loading: loadingCategories,
        error: errorCategories,
        data: categoriesData,
    } = useQuery<GetCategoriesQuery>(getCategoriesQuery, { fetchPolicy: 'cache-first' });
    const categories = categoriesData?.categories ?? [];
    const category = useMemo(
        () => categories.find(item => item.id === categoryId) ?? categories.find(item => item.templateId === categoryId),
        [categories, categoryId],
    );
    const { data: templatesData } = useQuery<GetTemplatesQuery>(getTemplatesQuery, { fetchPolicy: 'cache-first' });
    if (templatesData) setTemplateTranslations(templatesData.templateCategories);
    const externalTemplate = useMemo(
        () => getCategoryExternalTemplate(category, templatesData?.templateCategories),
        [category, templatesData],
    );
    const {
        loading: loadingAchievements,
        error: errorAchievements,
        data: achievementsData,
    } = useQuery<GetCategoryAchievementsQuery, GetCategoryAchievementsQueryVariables>(getCategoryAchievements, {
        variables: { categoryId: category?.id ?? '' },
        skip: !category,
        fetchPolicy: 'cache-first',
    });

    const achievements = achievementsData?.achievements ?? [];
    const categoryName = category ? translateTemplateText(t, category.templateId, 'name', category.name) : '';
    const externalTemplateName = externalTemplate ? translateTemplateText(t, externalTemplate.id, 'name', externalTemplate.name) : '';

    const allQuestIds = useMemo(
        () => Array.from(new Set(achievements.flatMap(a => a.achievementConditions).flatMap(c => c.questIds))),
        [achievements],
    );

    const { quests, loading: loadingQuests } = useCachedQuestsWithFallback(allQuestIds);

    // Sort achievements: completed first, then by name
    const sortedAchievements = useMemo(() => {
        return [...achievements].sort((a, b) => {
            const aCompleted = !!a.achievedOn;
            const bCompleted = !!b.achievedOn;
            if (aCompleted && !bCompleted) return -1;
            if (!aCompleted && bCompleted) return 1;
            if (aCompleted && bCompleted) return dayjs(b.achievedOn!).isAfter(dayjs(a.achievedOn!)) ? 1 : -1;
            return translateTemplateText(t, a.templateId, 'name', a.name).localeCompare(translateTemplateText(t, b.templateId, 'name', b.name));
        });
    }, [achievements, t]);

    const handleNewAchievement = useCallback(() => {
        if (!category) return;
        const path = achievementCreatePath.replace(':categoryId?', category.id);
        navigate(path);
    }, [category, navigate]);

    const handleBack = useCallback(() => navigate(-1), [navigate]);
    const handleOpenExternal = useCallback(() => openExternalLink(externalTemplate?.externalLink), [externalTemplate]);
    const handleClose = useCallback(() => {
        if (miniApp.close.isAvailable()) {
            miniApp.close();
            return;
        }
        window.close();
    }, []);

    const showButtons = !!category && (!category.system || !!externalTemplate) && !isExternalCategoryDeepLink;
    const showExternalButton = !!externalTemplate && !isExternalCategoryDeepLink;

    if (loadingAchievements || loadingCategories) return <AchievementListSkeleton />;
    if (errorAchievements || errorCategories) return <AchievementListSkeleton />;

    const handleAchievementClick = (achievementId: string) => {
        const path = achievementViewPath.replace(':id', achievementId);
        navigate(path);
    };

    return (
        <>
            <Page
                back
                insetEnabled={false}
                backClick={isExternalCategoryDeepLink ? handleClose : undefined}
                footer={
                    <ButtonDock
                        secondary={{
                            text: isExternalCategoryDeepLink ? t('profile.premium.close') : t('actions.back'),
                            onClick: isExternalCategoryDeepLink ? handleClose : handleBack,
                            navigation: isExternalCategoryDeepLink ? 'close' : 'back',
                        }}
                        main={
                            isExternalCategoryDeepLink
                                ? undefined
                                : {
                                      text: showExternalButton ? t('achievementEditor.open', { name: externalTemplateName }) : t('achievementEditor.new'),
                                      onClick: showExternalButton ? handleOpenExternal : handleNewAchievement,
                                      enabled: showButtons,
                                  }
                        }
                    />
                }
            >
                <Stack direction="row" alignItems="center" justifyContent="center" gap={1} mt={1} mb={3}>
                    {category && (
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 2,
                                background: getColor(category.iconColor),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CategoryIcon
                                icon={category.icon}
                                iconColor={category.iconColor}
                                customIcon={category.customIcon}
                                type="filled"
                                color="white"
                                size={20}
                                sx={category.customIcon ? { width: '100%', height: '100%', borderRadius: 'inherit' } : undefined}
                            />
                        </Box>
                    )}
                    <Text weight="2" style={{ fontSize: '18px' }}>
                        {categoryName || t('achievementEditor.title')}
                    </Text>
                </Stack>

                {achievements.length === 0 ? (
                    <Stack direction="column" justifyContent="center" alignItems="center" height="100%">
                        <Placeholder
                            header={t('achievementEditor.emptyTitle')}
                            description={t('achievementEditor.emptyDescription')}
                            style={{ padding: 0 }}
                        >
                            <FocusLeePlaceholderImage kind="achievements" />
                        </Placeholder>
                    </Stack>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            px: '16px',
                            pb: miniButtonDockScrollBottomPad,
                        }}
                    >
                        <Box sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden', ...liquidGlassRowShadowSx }}>
                            <Box sx={{ borderRadius: 'inherit', ...liquidGlassSurfaceFillSx }}>
                                {sortedAchievements.map((achievement, index) => (
                                    <AchievementListItem
                                        key={achievement.id}
                                        achievement={achievement}
                                        quests={quests}
                                        questsLoading={loadingQuests}
                                        showDivider={index > 0}
                                        onClick={() => handleAchievementClick(achievement.id)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Page>
        </>
    );
};
